export type NestedObject = { [key: string]: NestedValue };
export type NestedValue = NestedObject | NestedValue[] | string | number | boolean | null | undefined;

/**
 * Get object value safely if the object is deeply nested
 * @param keys - Object key you want to traverse
 * @param object - The targeted object
 */
export function getSafely<Result extends NestedValue = NestedValue>(
  keys: ReadonlyArray<string | number>,
  object?: NestedValue
): Result {
  // The accumulator is only null-checked (not shape-checked) — matching the original runtime behavior,
  // so the traversal step needs an assertion to index an arbitrary nested value.
  const result = keys.reduce<NestedValue>(
    (acc, val) => (acc === null || acc === undefined ? null : (acc as NestedObject)[val]),
    object
  );
  return result as Result;
}

/**
 * Get image src
 * @param file - File object
 * @param callback - Callback function which recieve the result
 */
export function previewImage(file: Blob, callback: (result: FileReader["result"]) => void): void {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    callback(reader.result);
  };
}

/**
 * Helper to reselect dropdown value to implement language changes
 * @param selected - array or object containing selected dropdown value
 * @param options - list of object to compare and get the same value
 */
export function reselect<Option extends { value: Value }, Value>(
  selected: Option | Option[],
  options: Option[]
): Option | Option[] {
  const isSingle = !Array.isArray(selected);

  if (isSingle) {
    return options.filter((item) => item.value === selected.value)[0];
  } else {
    const selectedValue = selected.map((x) => x.value);
    return options.filter((item) => selectedValue.includes(item.value));
  }
}
